import React, { Component, ReactNode } from 'react'
import classnames from 'classnames'
import { Trans, t } from '@lingui/macro'
import { Box } from '@chakra-ui/react'

import { ITopicVersion } from '../types'
import { ItemList } from '../../../components/common/list/ItemList'

import { PathwayResourceListItem } from './PathwayResourceListItem'
import IconPlus from '../../../components/common/icon/IconPlusInCircle'
import Loader from '../../../components/common/loader/Loader'

import { SelectTopicsModal } from './SelectTopicsModal'

import IconBan from 'front-end/src/components/common/icon/IconBan'
import { PathwayDetailFieldsForManageFragment } from '../ManagePathwayPage.codegen'
import { ButtonOutline, ButtonIcon } from 'front-end/src/components'
import {
  SortableContainer,
  SortableElement,
  SortableHandle
} from 'react-sortable-hoc'
import IconDrag from '../../../components/common/icon/IconDrag'
import IconEmptyResources from 'public/assets/img/empty-resources.svg'

interface IState {
  showAddTopicModal: boolean
  loadingTopicId: number | null
}

interface IProps {
  pathwayTopics: Omit<
    PathwayDetailFieldsForManageFragment,
    'currentTopicVersions' // should be passed separately
  > | null
  pathwayControls: ReactNode
  loading: boolean
  fixedOrder: boolean
  onTopicRemoved: (topicId: number) => Promise<void>
  onTopicOrderChange: (params: {
    oldIndex: number
    newIndex: number
  }) => Promise<void>
  refetchTopics: () => void
  resources:
    | NonNullable<PathwayDetailFieldsForManageFragment>['currentTopicVersions']
    | null
}

export class PathwayResourcesTab extends Component<IProps, IState> {
  state = {
    showAddTopicModal: false,
    loadingTopicId: null
  }

  handleRemoveTopic = async (topicVersion: ITopicVersion): Promise<void> => {
    const { onTopicRemoved } = this.props

    this.setState({
      loadingTopicId: topicVersion.topic.id
    })
    try {
      await onTopicRemoved(topicVersion.topic.id)
    } finally {
      this.setState({
        loadingTopicId: null
      })
    }
  }

  handleDragChangeOrder = async ({ oldIndex, newIndex }): Promise<void> => {
    const { loadingTopicId } = this.state
    const { resources } = this.props

    if (oldIndex === newIndex || loadingTopicId !== null || !resources) {
      return
    }
    this.setState({ loadingTopicId: resources[newIndex].id })

    await this.props.onTopicOrderChange({ oldIndex, newIndex })

    this.setState({ loadingTopicId: null })
  }

  handleAddTopic = async () => {
    await this.props.refetchTopics()

    this.setState({
      showAddTopicModal: false
    })
  }

  handleAddTopicToggle = (showAddTopicModal: boolean): void => {
    this.setState({ showAddTopicModal })
  }

  renderControls(): ReactNode {
    const { pathwayControls } = this.props

    return (
      <>
        {pathwayControls}
        <ButtonOutline
          onClick={() => this.handleAddTopicToggle(true)}
          leftIcon={<IconPlus />}
          mb="16px"
        >
          <Trans>Add to pathway</Trans>
        </ButtonOutline>
      </>
    )
  }

  renderEmpty(): ReactNode {
    return (
      <>
        <img src={IconEmptyResources} alt={t`Empty resource`} />
        <h2>
          <Trans>Build your pathway</Trans>
        </h2>
        <p>
          <Trans>
            Start building a learning pathway by clicking <br />
            on the ‘Add to pathway button above
          </Trans>
        </p>
      </>
    )
  }

  render(): ReactNode {
    const { showAddTopicModal, loadingTopicId } = this.state
    const { resources, pathwayTopics, loading, fixedOrder } = this.props

    if (loading || !resources || !pathwayTopics) {
      return <Loader />
    }

    return (
      <SortableDiv
        lockAxis="y"
        useDragHandle
        onSortEnd={this.handleDragChangeOrder}
        helperClass="dragging"
      >
        <ItemList
          showFooter={false}
          className={classnames('PathwayTopics__list', {
            'PathwayProgress__fixed-order': fixedOrder
          })}
          items={resources}
          itemsCount={pathwayTopics.currentTopicVersionsCount ?? 0}
          renderItem={(topicVersion: ITopicVersion, index) => (
            <PathwayResourceElement
              key={topicVersion.id}
              index={index as number}
              DragHandle={DragHandle}
              topicVersion={topicVersion}
              fixedOrder={fixedOrder}
              removeButton={
                <ButtonIcon
                  circular
                  icon={<IconBan />}
                  hint={t`Remove`}
                  aria-label={t`Remove`}
                  onClick={async () => {
                    await this.handleRemoveTopic(topicVersion)
                    this.props.refetchTopics()
                  }}
                />
              }
              isLoading={loadingTopicId === topicVersion.topic.id}
            />
          )}
          header={this.renderControls()}
          emptyState={this.renderEmpty()}
        />

        {showAddTopicModal && (
          <SelectTopicsModal
            modalProps={{
              header: t`Add to pathway`,
              subHeader: t`Choose resources to add to this pathway`,
              className: 'PathwayAddTopicModal'
            }}
            pathwayId={pathwayTopics.id}
            selectedTopicIds={resources.map(
              (topicVersion) => topicVersion.topic.id
            )}
            onAddTopics={this.handleAddTopic}
            onCancel={() => this.handleAddTopicToggle(false)}
          />
        )}
      </SortableDiv>
    )
  }
}

const SortableDiv = SortableContainer((props) => <Box {...props} />)
const PathwayResourceElement = SortableElement(PathwayResourceListItem)
const DragHandle = SortableHandle(() => (
  <ButtonIcon
    circular
    cursor="row-resize"
    icon={<IconDrag position="relative" top="1px" />}
    hint={t`Drag`}
    aria-label={t`Drag`}
  />
))
